import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Badge, Card, Row, Col } from 'react-bootstrap';
import { FileText, CheckCircle, Clock, X } from 'lucide-react';
import { apiClient } from '../api';

const BulkVoucherModal = ({ show, onHide, selectedTripIds = [], trips = [] }) => {
    const [notes, setNotes] = useState('');

    const selectedTripsData = useMemo(() => {
        if (!selectedTripIds || selectedTripIds.length === 0) return [];
        return trips.filter(t => selectedTripIds.includes(t.id));
    }, [trips, selectedTripIds]);

    const getTripFinancials = (t) => {
        const basePrice = (t.final_price !== null && t.final_price !== undefined && t.final_price !== '') 
            ? (parseFloat(t.final_price) || 0) 
            : (parseFloat(t.estimated_price) || 0);
        const waitAmount = parseFloat(t.waiting_time_amount) || 0;
        
        let extrasAmount = 0;
        let extraItems = [];
        if (Array.isArray(t.extra_services)) {
            extraItems = t.extra_services;
        } else if (typeof t.extra_services === 'string') {
            try {
                const parsed = JSON.parse(t.extra_services);
                if (Array.isArray(parsed)) extraItems = parsed;
            } catch (e) {}
        }
        extrasAmount = extraItems.reduce((acc, s) => acc + (parseFloat(s?.price) || 0), 0);

        const total = basePrice + waitAmount + extrasAmount;
        return { basePrice, waitAmount, extrasAmount, total };
    };

    const totalAmount = useMemo(() => {
        return selectedTripsData.reduce((acc, t) => {
            const { total } = getTripFinancials(t);
            return acc + total;
        }, 0);
    }, [selectedTripsData]);

    const clientsList = useMemo(() => {
        const set = new Set();
        selectedTripsData.forEach(t => {
            const name = t.client?.name || t.client_name;
            if (name) set.add(name);
        });
        return Array.from(set);
    }, [selectedTripsData]);

    // Inicializar reseña sugerida al abrir
    useEffect(() => {
        if (show && selectedTripsData.length > 0) {
            const idsList = selectedTripsData.map(t => `EM-${String(t.id).padStart(5, '0')}`).join(', ');
            const clientStr = clientsList.length === 1 ? ` para ${clientsList[0]}` : '';
            setNotes(`Comprobante consolidado correspondiente a los servicios ${idsList}${clientStr}. Traslado ejecutivo.`);
        }
    }, [show, selectedTripsData, clientsList]);

    const formatMoney = (val) => {
        return `$${Math.round(val || 0).toLocaleString('es-CL')}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const handleDownload = (isPaid) => {
        if (!selectedTripIds || selectedTripIds.length === 0) return;
        const baseUrl = apiClient.defaults.baseURL || '';
        const idsParam = selectedTripIds.join(',');
        const encodedNotes = encodeURIComponent((notes || '').trim());
        const url = `${baseUrl}/transport/trips/bulk-voucher/pdf?trip_ids=${idsParam}&is_paid=${isPaid}${encodedNotes ? `&notes=${encodedNotes}` : ''}`;
        window.open(url, '_blank');
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="h6 fw-bold d-flex align-items-center gap-2">
                    <FileText size={20} className="text-primary" />
                    Generar Comprobante Consolidado
                    <Badge bg="primary">{selectedTripsData.length} Servicios</Badge>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-3">
                {/* TARJETA DE RESUMEN EJECUTIVO */}
                <Card className="mb-3 border-0 bg-primary bg-opacity-10 shadow-none">
                    <Card.Body className="p-3">
                        <Row className="align-items-center">
                            <Col md={7}>
                                <div className="small text-muted mb-1">Cliente / Cuenta:</div>
                                <div className="fw-bold text-dark fs-6">
                                    {clientsList.length > 0 ? clientsList.join(', ') : 'PARTICULAR / DIRECTO'}
                                </div>
                                <div className="small text-muted mt-1">
                                    {selectedTripsData.length} reservas seleccionadas para emisión conjunta
                                </div>
                            </Col>
                            <Col md={5} className="text-md-end mt-2 mt-md-0 border-start-md">
                                <div className="small text-muted mb-1">Monto Total Consolidado:</div>
                                <div className="fw-bold text-primary fs-4">
                                    {formatMoney(totalAmount)}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* TABLA DE VIAJES SELECCIONADOS */}
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small fw-bold text-secondary">Desglose de Servicios Seleccionados:</span>
                    </div>
                    <div className="table-responsive border rounded" style={{ maxHeight: '180px' }}>
                        <Table hover size="sm" className="mb-0 small align-middle">
                            <thead className="bg-light sticky-top">
                                <tr>
                                    <th style={{ width: '85px' }}>ID</th>
                                    <th>Fecha/Hora</th>
                                    <th>Pasajero</th>
                                    <th>Ruta</th>
                                    <th className="text-end" style={{ width: '120px' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedTripsData.map(t => {
                                    const { waitAmount, extrasAmount, total } = getTripFinancials(t);
                                    const hasExtrasOrWait = waitAmount > 0 || extrasAmount > 0;
                                    return (
                                        <tr key={t.id}>
                                            <td className="fw-bold text-primary">EM-{String(t.id).padStart(5, '0')}</td>
                                            <td className="text-muted">{formatDate(t.scheduled_time)}</td>
                                            <td className="fw-semibold text-truncate" style={{ maxWidth: '140px' }}>
                                                {t.passenger_name || 'PARTICULAR'}
                                            </td>
                                            <td className="text-muted text-truncate" style={{ maxWidth: '220px' }}>
                                                <span className="text-dark">{t.pickup_address || '---'}</span> ➔ {t.dropoff_address || '---'}
                                            </td>
                                            <td className="text-end fw-bold">
                                                <div>{formatMoney(total)}</div>
                                                {hasExtrasOrWait && (
                                                    <div className="text-muted fw-normal" style={{ fontSize: '10px' }}>
                                                        {waitAmount > 0 && `Esp: ${formatMoney(waitAmount)}`}
                                                        {waitAmount > 0 && extrasAmount > 0 && ' | '}
                                                        {extrasAmount > 0 && `Ext: ${formatMoney(extrasAmount)}`}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </div>

                {/* CAMPO DE RESEÑA / OBSERVACIÓN */}
                <Form.Group className="mb-2">
                    <Form.Label className="small fw-bold text-dark d-flex align-items-center gap-1">
                        Reseña / Observación en el Comprobante:
                        <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                            (Aparecerá impresa en el recuadro superior del PDF)
                        </span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ej: Servicios correspondientes a traslado de personal corporativo..."
                        className="small"
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center bg-light">
                <Button variant="outline-secondary" size="sm" onClick={onHide} className="d-flex align-items-center gap-1">
                    <X size={15} /> Cancelar
                </Button>

                <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted me-1">Generar con timbre:</span>
                    <Button 
                        variant="success" 
                        size="sm" 
                        onClick={() => handleDownload(true)} 
                        className="fw-bold d-flex align-items-center gap-1 shadow-sm"
                    >
                        <CheckCircle size={15} /> 💰 PAGADO
                    </Button>
                    <Button 
                        variant="warning" 
                        size="sm" 
                        onClick={() => handleDownload(false)} 
                        className="text-dark fw-bold d-flex align-items-center gap-1 shadow-sm"
                    >
                        <Clock size={15} /> ⏳ PENDIENTE
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkVoucherModal;
